import { Stack } from 'component-library';
import { TFunction } from 'i18next';
import { FC, HTMLAttributes } from 'react';
import { useTranslation } from 'react-i18next';
import { NameType, ValueType } from 'recharts/types/component/DefaultTooltipContent';
import { Cell, Pie, PieChart, TooltipProps } from 'recharts-overriden';

import { UnitEnum } from '@/api/rest/resources/types/units';
import { Chart } from '@/components/Charts/components/Chart';
import { ChartLegendBar } from '@/components/Charts/components/LegendBar';
import {
  ChartTooltip,
  ChartTooltipTextPrimary,
  ChartTooltipTextSecondary,
} from '@/components/Charts/components/Tooltip';
import { ChartLegendBarItem } from '@/components/Charts/components/types';
import { getDisplayNumber } from '@/hooks/useDisplayNumber';
import { Logger } from '@/lib/logs/logger';

import { NoChartData } from './NoChartData';

export type DonutChartData = {
  color: string;
  name: string;
  value: number;
  unit: UnitEnum;
};

const INNER_RADIUS = 80;
const OUTER_RADIUS = 120;
const LABEL_THRESHOLD = 2;

type DonutChartProps = {
  data: DonutChartData[];
  showLabel?: boolean;
  innerRadius?: number;
  outerRadius?: number;
  labelThreshold?: number;
};

export const DonutChart: FC<HTMLAttributes<HTMLDivElement> & DonutChartProps> = ({
  data,
  showLabel = true,
  outerRadius = OUTER_RADIUS,
  innerRadius = INNER_RADIUS,
  labelThreshold = LABEL_THRESHOLD,
  ...delegated
}) => {
  const { t } = useTranslation();

  if (data.length === 0) {
    Logger.error('Not enough data to render chart');
    return (
      <div className='relative flex h-full w-full flex-col justify-center' {...delegated}>
        <NoChartData />
      </div>
    );
  }

  const chartConfig = data.reduce(
    (acc, item) => {
      acc[item.name] = { label: item.name, color: item.color };
      return acc;
    },
    {} as Record<string, { label: string; color: string }>,
  );

  const totalValue = data.reduce((sum, d) => sum + d.value, 0);

  const handleShowLabel = (value: number): boolean => {
    if (!showLabel) {
      return false;
    }

    const percentage = totalValue > 0 ? (value / totalValue) * 100 : 0;

    return percentage >= labelThreshold;
  };

  const handleLabelRender = ({ value }: { value: number }) => {
    const shouldShowLabel = handleShowLabel(value);

    return shouldShowLabel ? getDisplayLabel({ value, totalValue, t }) : null;
  };

  const handleLabelLineRender = ({
    points,
    value,
    color,
  }: {
    points: { x: number; y: number }[];
    value: number;
    color: string;
  }) => {
    const startPoints = points?.at(0) ?? null;
    const endPoints = points?.at(1) ?? null;

    if (!handleShowLabel(value) || !startPoints || !endPoints) {
      return <line />; // same as undefined, but expected by TS for labelLine
    }

    return <line x1={startPoints.x} y1={startPoints.y} x2={endPoints.x} y2={endPoints.y} stroke={color} />;
  };

  return (
    <>
      <Chart.Container {...delegated} data={data} config={chartConfig} height={300}>
        <PieChart>
          <Pie
            data={data}
            dataKey='value'
            innerRadius={innerRadius}
            outerRadius={outerRadius}
            startAngle={90}
            endAngle={450}
            paddingAngle={1}
            isAnimationActive={true}
            label={handleLabelRender}
            labelLine={handleLabelLineRender}
          >
            {data.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.color} className='outline-none' />
            ))}
          </Pie>
          <Chart.Tooltip content={(props) => <CustomTooltip {...props} totalValue={totalValue} />} />
        </PieChart>
      </Chart.Container>
      <CustomLegendBar data={data} />
    </>
  );
};

const getDisplayLabel = ({ value, totalValue, t }: { value: number; totalValue: number; t: TFunction }): string => {
  const percentage = totalValue > 0 ? (value / totalValue) * 100 : null;
  if (percentage === null) {
    return '';
  }

  return `${getDisplayNumber(String(percentage), window.navigator.language)} ${t('global.ui.unit.%')}`;
};

const CustomLegendBar = ({ data }: { data: DonutChartData[] }) => {
  const legendItems: ChartLegendBarItem[] = data.map((item) => ({
    label: item.name,
    color: item.color,
    shape: 'area',
  }));

  return (
    <ChartLegendBar
      items={legendItems}
      className='sm:ml-[28px] w-full justify-center'
      data-testid='donut-chart-legend'
    />
  );
};

const CustomTooltip = ({ active, payload, totalValue }: TooltipProps<ValueType, NameType> & { totalValue: number }) => {
  const { t } = useTranslation();
  if (!active || !payload?.length) {
    return null;
  }

  const item = payload[0]?.payload as DonutChartData | undefined;
  if (!item) {
    return null;
  }

  const percentageDisplay = getDisplayLabel({ value: item.value, totalValue, t });
  const unit = t(`global.ui.unit.${item.unit}`);
  const displayValue = `${getDisplayNumber(String(item.value), window.navigator.language)} ${unit}`;

  return (
    <ChartTooltip>
      <Stack spacing={8} direction='row'>
        <ChartTooltipTextPrimary>{item.name}</ChartTooltipTextPrimary>
        <ChartTooltipTextSecondary>
          {displayValue} ({percentageDisplay})
        </ChartTooltipTextSecondary>
      </Stack>
    </ChartTooltip>
  );
};

export { CustomTooltip as DonutChartCustomTooltip };
